import type {
  AudienceFilterType,
  ExpressionNodeType,
} from "@intelligems/ig-types/dtos/audiences";
import type { RedirectFilter } from "@intelligems/ig-types/dtos/redirects";
import type { TrafficConfigFilterType } from "@intelligems/ig-types/dtos/experience-page-targeting";
import type { UrlMatch } from "@intelligems/ig-types/dtos/url-match";

import { postfixToExpressionList } from "./expression";

type GenericRedirect = {
  originUrl: string;
  destinationUrl?: string | null | undefined;
  isTemplateTest: boolean;
  isThemeTest: boolean;
  filter: RedirectFilter;
};

type GenericPageTargeting = {
  expression?: ExpressionNodeType[] | null;
  filterType: AudienceFilterType | TrafficConfigFilterType;
  filter?: UrlMatch | null | undefined;
  value: string;
};

export const getPreviewPathShared = (
  redirects: GenericRedirect[],
  pageTargeting: GenericPageTargeting[],
  useOriginRedirectUrl?: boolean
) => {
  let previewPath: string = "";
  if (pageTargeting.length) {
    const targetedPath = findPageTargeting(pageTargeting);
    if (targetedPath) {
      previewPath = `/${targetedPath.value}`;
    }
  }

  if (redirects.length && !previewPath) {
    const firstRedirectFound = redirects.find(
      (redirect) =>
        redirect.originUrl &&
        (useOriginRedirectUrl || redirect.destinationUrl) &&
        !redirect.isThemeTest &&
        !redirect.isTemplateTest
    );
    if (firstRedirectFound) {
      previewPath = `${
        firstRedirectFound.filter === "matchesExactly" ||
        firstRedirectFound.filter === "none"
          ? ""
          : "/"
      }${firstRedirectFound[useOriginRedirectUrl ? "originUrl" : "destinationUrl"]}`;
    }
  }

  return previewPath;
};

type TargetedPath = Pick<GenericPageTargeting, "filter" | "value">;

const findPageTargeting = (
  pageTargeting: GenericPageTargeting[]
): TargetedPath | undefined => {
  for (const value of pageTargeting) {
    if (value.expression) {
      const result = postfixToExpressionList(value.expression, "pageTargeting")
        .flatMap((group) => group.queries)
        .find((query) => query.filter === "equals" && query.type === "urlPath");
      if (result) {
        return {
          filter: result.filter,
          value: result.value,
        };
      }
      continue;
    }

    if (value.filter === "equals" && value.filterType === "url") {
      return value;
    }
  }

  return undefined;
};
